import tokens from '@viewthespace/style-dictionary/assets/json/tokens.json';

import { baseStyles } from './base';

type Params = {
  colors: any;
  success?: boolean;
  invalid?: boolean;
  icon?: boolean;
  disabled?: boolean;
};

const DROPDOWN_HEIGHT = '3em';

export function getStyles({
  colors,
  success,
  invalid,
  icon,
  disabled,
}: Params) {
  return {
    control: (styles: any, state: any) => {
      let colorsFocused: any;

      if (!disabled) {
        colorsFocused = state.isFocused
          ? colors.textColorFocused
          : colors.textColor;
      } else {
        colorsFocused = colors.textColorDisabled;
      }

      const border = '1px';
      const borderGap = 0;
      const colorHover = invalid
        ? tokens.color.border.critical
        : colors.borderHover;
      let color = state.selectProps.menuIsOpen
        ? colors.primaryColor
        : colors.borderColor;

      if (success) {
        color = tokens.color.text.success;
      } else if (invalid) {
        color = tokens.color.border.critical;
      }

      return {
        overflow: 'hidden',
        left: borderGap,
        right: borderGap,
        top: borderGap,
        bottom: borderGap,
        border: `${border} solid ${color}`,
        backgroundColor: colors.background,
        ...baseStyles.control,
        ...baseStyles.row,

        color: colorsFocused,
        ':hover': {
          borderColor: colorHover,
        },
        ':focus-within': {
          borderColor: colorHover,
        },
        minHeight: DROPDOWN_HEIGHT,
        height: DROPDOWN_HEIGHT,
        opacity: disabled ? 0.6 : 1,
      };
    },
    placeholder: (styles: any) => ({
      ...styles,
      ...baseStyles.placeholder,
    }),
    indicatorSeparator: (styles: any) => ({
      ...styles,
      display: 'none',
    }),
    dropdownIndicator: (styles: any, state: any) => {
      let color: any;

      if (disabled) {
        color = colors.textColorDisabled;
      } else {
        color = state.selectProps.menuIsOpen
          ? colors.primaryColor
          : colors.dropdownIndicator;
      }

      return {
        ...styles,
        ...baseStyles.dropdownIndicator,
        color,
        padding: '0.25em',
        ':hover': {
          color: colors.primaryColors,
        },
      };
    },
    indicatorsContainer: (styles: any) => ({
      ...styles,
      height: '100%',
      alignItems: 'center',
    }),
    menu: (styles: any) => ({
      ...styles,
      ...baseStyles.menu,
      backgroundColor: colors.background,
    }),
    valueContainer: (styles: any) => {
      // Keep v5's default `display` (grid for single, flex for multi-with-value)
      // so SingleValue and the Input wrapper overlap at the same grid area.
      // Forcing `display: flex` here breaks that overlap and causes clicks on
      // the SingleValue to miss the underlying input.
      return {
        ...styles,
        ...baseStyles.valueContainer,
        padding: '0 0.875em',
        height: '100%',
        alignItems: 'center',
        flex: 1,
        overflow: 'hidden',
      };
    },
    input: (styles: any) => {
      // Trust v5's default sizing (the Input wrapper auto-collapses to 0 width
      // when there's no typed input via `grid-template-columns: 0 min-content`).
      // Previously this returned `position: absolute; width: 1px` which created
      // a 1px hot-spot at the top-left and was the reason only the first letter
      // of the displayed value opened the menu.
      return icon
        ? { ...styles, ...baseStyles.inputWithIcon }
        : { ...styles, ...baseStyles.input };
    },
    menuList: (styles: any) => ({
      ...styles,
      ...baseStyles.menuList,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 1000000,
    }),
    option: (styles: any, state: any) => {
      if (state.isDisabled) {
        return {
          ...styles,
          ...baseStyles.option,
          color: colors.textColorDisabled,
          opacity: 0.5,
          cursor: 'not-allowed',
          backgroundColor: colors.background,
        };
      }

      let customStyle: any = {
        color: colors.textColor,
        backgroundColor: colors.background,
        ':hover': {
          backgroundColor: colors.hover,
          color: colors.hoverText,
        },
        display: 'flex',
        justifyContent: 'left',
      };

      if (state.isFocused) {
        customStyle = {
          ...customStyle,
          color: colors.textColorFocused,
          backgroundColor: colors.hover,
        };
      }

      return {
        ...styles,
        ...baseStyles.option,
        ...customStyle,
      };
    },
    singleValue: (styles: any, state: any) => {
      const baseStyles = {
        ...styles,
        color: state.selectProps.menuIsOpen ? colors.onPressControlTextColor : colors.text,
        gridArea: '1 / 1 / 2 / 3', // Ensure it spans the full width in CSS Grid (v5)
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      };

      return baseStyles;
    },
    optionCheckboxContainer: () => ({ ...baseStyles.optionCheckboxContainer }),
  };
}
